import { useState, useCallback, useMemo } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { Owner } from "../data/types";
import {
  filterAssets,
  sortAssets,
  computeAssetStats,
  DEFAULT_ASSET_FILTERS,
} from "../lib/assetUtils";
import type { Asset, AssetFilterState, AssetCategory, AssetSortField } from "../lib/assetUtils";
import { buildBreadcrumbs, getItemsInFolder, countFolderContents } from "../lib/folderUtils";
import type { FolderItem, Breadcrumb } from "../lib/folderUtils";

export interface AssetFolder {
  id: string;
  name: string;
  parentId: string | null;
  owner: Owner;
  createdAt: number;
}

export function useAssets() {
  // ── Convex queries ──
  const convexAssets = useQuery(api.assets.list);
  const convexFolders = useQuery(api.assetFolders.list);

  // ── Convex mutations ──
  const createMut = useMutation(api.assets.create);
  const updateMut = useMutation(api.assets.update);
  const moveToFolderMut = useMutation(api.assets.moveToFolder);
  const replaceFileMut = useMutation(api.assets.replaceFile);
  const restoreVersionMut = useMutation(api.assets.restoreVersion);
  const removeMut = useMutation(api.assets.remove);
  const generateUploadUrlMut = useMutation(api.assets.generateUploadUrl);

  const createFolderMut = useMutation(api.assetFolders.create);
  const renameFolderMut = useMutation(api.assetFolders.rename);
  const removeFolderMut = useMutation(api.assetFolders.remove);

  // ── Local UI state ──
  const [filters, setFiltersState] = useState<AssetFilterState>(DEFAULT_ASSET_FILTERS);
  const [sortField, setSortField] = useState<AssetSortField>("updatedAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);

  // ── Map Convex assets to app Asset type ──
  const assets: Asset[] = useMemo(() => {
    if (!convexAssets) return [];
    return convexAssets.map((a) => ({
      id: a._id,
      title: a.title,
      description: a.description,
      category: a.category as AssetCategory,
      owner: a.owner,
      storageId: a.storageId,
      fileName: a.fileName,
      fileType: a.fileType,
      fileSize: a.fileSize,
      fileUrl: a.fileUrl,
      content: a.content,
      folderId: (a.folderId as string | undefined) ?? null,
      createdAt: a._creationTime,
      updatedAt: a._creationTime,
    }));
  }, [convexAssets]);

  // ── Map Convex folders ──
  const folders: AssetFolder[] = useMemo(() => {
    if (!convexFolders) return [];
    return convexFolders.map((f) => ({
      id: f._id,
      name: f.name,
      parentId: (f.parentId as string | undefined) ?? null,
      owner: f.owner as Owner,
      createdAt: f._creationTime,
    }));
  }, [convexFolders]);

  // ── Build unified FolderItem list for pure utility functions ──
  const allItems: FolderItem[] = useMemo(() => {
    const folderItems: FolderItem[] = folders.map((f) => ({
      id: f.id,
      type: "folder" as const,
      name: f.name,
      parentId: f.parentId,
    }));
    const assetItems: FolderItem[] = assets.map((a) => ({
      id: a.id,
      type: "asset" as const,
      name: a.title,
      parentId: a.folderId ?? null,
    }));
    return [...folderItems, ...assetItems];
  }, [folders, assets]);

  // ── Breadcrumbs ──
  const breadcrumbs: Breadcrumb[] = useMemo(
    () => buildBreadcrumbs(currentFolderId, allItems),
    [currentFolderId, allItems],
  );

  // ── Items in current folder ──
  const currentFolderItems = useMemo(
    () => getItemsInFolder(currentFolderId, allItems),
    [currentFolderId, allItems],
  );

  // ── Assets in current folder (filtered + sorted) ──
  const assetsInFolder: Asset[] = useMemo(() => {
    const assetIds = new Set(
      currentFolderItems.filter((i) => i.type === "asset").map((i) => i.id),
    );
    const inFolder = assets.filter((a) => assetIds.has(a.id));
    const filtered = filterAssets(inFolder, filters);
    return sortAssets(filtered, sortField, sortDirection);
  }, [currentFolderItems, assets, filters, sortField, sortDirection]);

  // ── Folders in current folder ──
  const foldersInView: AssetFolder[] = useMemo(() => {
    const folderIds = new Set(
      currentFolderItems.filter((i) => i.type === "folder").map((i) => i.id),
    );
    return folders
      .filter((f) => folderIds.has(f.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [currentFolderItems, folders]);

  // ── Filtered + sorted ALL assets (for search across folders) ──
  const filteredAssets = useMemo(() => {
    const filtered = filterAssets(assets, filters);
    return sortAssets(filtered, sortField, sortDirection);
  }, [assets, filters, sortField, sortDirection]);

  // When searching, show results across all folders
  const isSearching = filters.search.length > 0;

  // ── Stats ──
  const stats = useMemo(() => computeAssetStats(assets), [assets]);

  // ── Actions ──
  const uploadFile = useCallback(async (file: File): Promise<Id<"_storage">> => {
    const uploadUrl = await generateUploadUrlMut();
    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: { "Content-Type": file.type },
      body: file,
    });
    const result = await response.json() as { storageId: Id<"_storage"> };
    return result.storageId;
  }, [generateUploadUrlMut]);

  const createAsset = useCallback(async (args: {
    title: string;
    description: string;
    category: AssetCategory;
    owner: Owner;
    file?: File;
    content?: string;
    folderId?: string;
  }) => {
    let storageId: Id<"_storage"> | undefined;
    let fileName: string | undefined;
    let fileType: string | undefined;
    let fileSize: number | undefined;

    if (args.file) {
      storageId = await uploadFile(args.file);
      fileName = args.file.name;
      fileType = args.file.type;
      fileSize = args.file.size;
    }

    return await createMut({
      title: args.title,
      description: args.description,
      category: args.category,
      owner: args.owner,
      storageId,
      fileName,
      fileType,
      fileSize,
      content: args.content,
      folderId: (args.folderId ?? currentFolderId) ? ((args.folderId ?? currentFolderId) as Id<"assetFolders">) : undefined,
    });
  }, [createMut, uploadFile, currentFolderId]);

  const updateAsset = useCallback(async (id: string, fields: {
    title?: string;
    description?: string;
    category?: AssetCategory;
    owner?: Owner;
    content?: string;
  }) => {
    await updateMut({ id: id as Id<"assets">, ...fields });
  }, [updateMut]);

  const moveAssetToFolder = useCallback(async (assetId: string, folderId: string | undefined) => {
    await moveToFolderMut({
      id: assetId as Id<"assets">,
      folderId: folderId as Id<"assetFolders"> | undefined,
    });
  }, [moveToFolderMut]);

  const replaceFile = useCallback(async (assetId: string, file: File, uploadedBy: Owner) => {
    const storageId = await uploadFile(file);
    await replaceFileMut({
      id: assetId as Id<"assets">,
      storageId,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      uploadedBy,
    });
  }, [replaceFileMut, uploadFile]);

  const restoreVersion = useCallback(async (assetId: string, versionNumber: number) => {
    await restoreVersionMut({
      assetId: assetId as Id<"assets">,
      versionNumber,
    });
  }, [restoreVersionMut]);

  const removeAsset = useCallback(async (id: string) => {
    await removeMut({ id: id as Id<"assets"> });
  }, [removeMut]);

  // ── Folder actions ──
  const createFolder = useCallback(async (name: string, owner: Owner) => {
    return await createFolderMut({
      name,
      parentId: currentFolderId ? (currentFolderId as Id<"assetFolders">) : undefined,
      owner,
    });
  }, [createFolderMut, currentFolderId]);

  const renameFolder = useCallback(async (id: string, name: string) => {
    await renameFolderMut({ id: id as Id<"assetFolders">, name });
  }, [renameFolderMut]);

  const removeFolder = useCallback(async (id: string) => {
    await removeFolderMut({ id: id as Id<"assetFolders"> });
  }, [removeFolderMut]);

  const navigateToFolder = useCallback((folderId: string | null) => {
    setCurrentFolderId(folderId);
  }, []);

  const navigateUp = useCallback(() => {
    if (breadcrumbs.length >= 2) {
      setCurrentFolderId(breadcrumbs[breadcrumbs.length - 2]!.id);
    }
  }, [breadcrumbs]);

  // ── Filter / sort setters ──
  const setFilters = useCallback((updates: Partial<AssetFilterState>) => {
    setFiltersState((f) => ({ ...f, ...updates }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_ASSET_FILTERS);
  }, []);

  const setSort = useCallback((field: AssetSortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortDirection("desc");
      }
      return field;
    });
  }, []);

  /** Get the count of items inside a folder */
  const getFolderCount = useCallback((folderId: string) => {
    return countFolderContents(folderId, allItems);
  }, [allItems]);

  return {
    // Assets
    assets: isSearching ? filteredAssets : assetsInFolder,
    allAssets: assets,
    stats,
    filters,
    sortField,
    sortDirection,
    viewMode,
    isLoading: !convexAssets || !convexFolders,
    isSearching,
    setFilters,
    resetFilters,
    setSort,
    setViewMode,
    createAsset,
    updateAsset,
    moveAssetToFolder,
    replaceFile,
    restoreVersion,
    removeAsset,

    // Folders
    folders,
    foldersInView,
    currentFolderId,
    breadcrumbs,
    navigateToFolder,
    navigateUp,
    createFolder,
    renameFolder,
    removeFolder,
    getFolderCount,
  };
}
